import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { InputAdornment } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { PageContainer, PageTitle } from '../styles';
import { DocsTabItem } from '../types';
import { DocsTabs } from './tabs/DocsTabs';
import { SearchDropdown } from './SearchDropdown';
import { useDocsSearch, DocsSearchResult } from '../search/useDocsSearch';
import {
  LayoutHeader,
  SearchWrapper,
  SearchInput,
  ClearButton,
} from './DocsLayout.styles';

interface DocsLayoutProps {
  tabs: DocsTabItem[];
  currentTab: number;
  onTabChange: (_event: React.ChangeEvent<{}>, newValue: number) => void;
  children: ReactNode;
  isSubPage?: boolean;
}

export function DocsLayout({
  tabs,
  currentTab,
  onTabChange,
  children,
  isSubPage = false,
}: DocsLayoutProps) {
  const navigate = useNavigate();
  const { term, results, isSearching, search, clear } = useDocsSearch();
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (result: DocsSearchResult) => {
      setShowDropdown(false);
      clear();
      navigate(result.location);
    },
    [clear, navigate],
  );

  return (
    <PageContainer>
      <LayoutHeader>
        <PageTitle variant="h4" style={{ marginBottom: 0 }}>
          Public Documentation
        </PageTitle>
        <SearchWrapper ref={wrapperRef}>
          <SearchInput
            placeholder="Search documentation..."
            variant="outlined"
            size="small"
            fullWidth
            value={term}
            onChange={e => {
              search(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => {
              if (term.length >= 2) setShowDropdown(true);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: term ? (
                <InputAdornment position="end">
                  <ClearButton
                    onClick={() => {
                      clear();
                      setShowDropdown(false);
                    }}
                  />
                </InputAdornment>
              ) : null,
            }}
          />
          {showDropdown && (
            <SearchDropdown
              results={results}
              term={term}
              isSearching={isSearching}
              onSelect={handleSelect}
            />
          )}
        </SearchWrapper>
      </LayoutHeader>
      <DocsTabs
        tabs={tabs}
        currentTab={currentTab}
        onChange={onTabChange}
        compact={isSubPage}
      />
      {isSubPage ? <div style={{ marginTop: 24 }}>{children}</div> : children}
    </PageContainer>
  );
}
